/**
 * Copyright (c) Microsoft. All rights reserved.
 */

import { IButtonProps, IButtonStyles, IconButton } from '@fluentui/react/lib/Button';
import { IIconStyleProps, IIconStyles } from '@fluentui/react/lib/Icon';
import { IStackProps, Stack } from '@fluentui/react/lib/Stack';
import { classNamesFunction } from '@fluentui/react/lib/Utilities';
import * as React from 'react';
import { defineMessages } from 'react-intl';
import { LuisTextField } from 'src/core/components/LuisTextField';
import { LuisTooltip } from 'src/core/components/LuisTooltip';
import { RBACTooltip } from 'src/core/components/RBACTooltip';
import { TruncatedText } from 'src/core/components/TruncatedText';
import { useLocalization } from 'src/core/hooks/useLocalization';
import { getScopedTheme, getStylistV2 } from 'src/core/themes/luisStylist';
import { mapUserRoleToUserActions } from 'src/core/utils/azureUtils';
import { partial } from 'src/core/utils/function';
import { LuisKeyCodes } from 'src/core/utils/keyCodes';
import { getSimpleEntityLimits } from 'src/luis/modules/entities/utils/entityCreationLimits';
import { LuisId } from 'src/luis/types/Resource';
import { SimpleEntityStore } from 'src/luis/types/stores/entities/SimpleEntityStore';

/**
 * Width used to represent indentation in the tree
 */
const indentWidth = 36;

const messages = defineMessages({
    addChildTitle: {
        id: 'entities.mlEntityDetailsCell.addChildTitle',
        defaultMessage: 'Add child'
    },
    addChildMaxCountTitle: {
        id: 'entities.mlEntityDetailsCell.addChildMaxCountTitle',
        defaultMessage: 'You have reached the maximum number of children for this entity'
    },
    deleteEntityTitle: {
        id: 'entities.mlEntityDetailsCell.deleteEntityTitle',
        defaultMessage: 'Delete'
    },
    collapseEntityTitle: {
        id: 'entities.mlEntityDetailsCell.collapseEntityTitle',
        defaultMessage: 'Collapse'
    },
    expandEntityTitle: {
        id: 'entities.mlEntityDetailsCell.expandEntityTitle',
        defaultMessage: 'Expand'
    }
});

const { styleComponent, getClassName } = getStylistV2('MLEntityInfoCell');
const mlEntityInfoCellTheme = getScopedTheme('MLEntityInfoCell');

const entityActionsContainerClassName = getClassName('EntityActionsContainer', { opacity: 0 });

const EntityContainer = styleComponent<IStackProps>(Stack)(
    'EntityContainer',
    (_, cssVars) => ({
        paddingLeft: cssVars.paddingLeft,
        $nest: {
            '&:hover': {
                $nest: {
                    [`& .${entityActionsContainerClassName}`]: {
                        opacity: 1
                    }
                }
            }
        }
    }),
    {
        cssVars: { paddingLeft: '' },
        cssVarsSetter: ({ level }: { level: number }) => ({
            paddingLeft: `${indentWidth * level}px`
        })
    }
);

const NameWrapper = styleComponent<IStackProps>(Stack)('NameWrapper', { minWidth: 0 });
const ActionsContainer = styleComponent<IStackProps>(Stack)('ActionsContainer', () => ({
    minWidth: 200,
    transition: 'opacity 150ms ease'
}));

const ToggleIcon = styleComponent<IButtonProps>(IconButton)(
    'ToggleIcon',
    (_, cssVars) => ({
        paddingLeft: '12px',
        paddingRight: '12px',
        fontSize: '12px',
        transform: cssVars.transform,
        transition: 'transform 150ms ease',
        $nest: {
            '&:hover, &:active': {
                background: 'transparent'
            }
        }
    }),
    {
        cssVars: { transform: '' },
        cssVarsSetter: ({ isCollapsed }: { isCollapsed: boolean }) => ({
            transform: isCollapsed ? 'rotate(-90deg)' : ''
        })
    }
);

const getButtonClassNames = classNamesFunction<IButtonProps, IButtonStyles>();
const controlsButtonStyles = getButtonClassNames({
    rootHovered: { backgroundColor: mlEntityInfoCellTheme.buttonHoverColor },
    iconDisabled: { opacity: 0.4 }
});

const getIconClassNames = classNamesFunction<IIconStyleProps, IIconStyles>();

const sharedIconStyles = { fontSize: '12px' };

const addChildEntityButtonIconStyles = getIconClassNames({
    root: { ...sharedIconStyles, color: mlEntityInfoCellTheme.addChildEntityButtonColor }
});

const deleteEntityButtonIconStyles = getIconClassNames({
    root: { ...sharedIconStyles, color: mlEntityInfoCellTheme.deleteEntityButtonColor }
});

const toggleEntityButtonIconStyles = getIconClassNames({
    root: {
        ...sharedIconStyles,
        color: mlEntityInfoCellTheme.toggleEntityButtonColor
    }
});

/**
 * Props for MLEntityInfoCell
 */
export type MLEntityDetailsProps = {
    rootEntityId: LuisId;
    parentEntityId: LuisId;
    entityStore: SimpleEntityStore;
    level: number;
    isCollapsed: boolean;
    kind: 'newEntity' | 'existingEntity';
    isSelected: boolean;
    onToggleEntityCreation: (parentId: LuisId) => void;
    onToggleCollapse: (id: string) => void;
    onCreateEntity: (rootEntityId: LuisId, parentEntityId: LuisId, entityName: string) => void;
    onRenameEntity: (entity: SimpleEntityStore, newName: string) => void;
    onRenameChildEntity: (rootEntityId: LuisId, entityId: LuisId, newName: string) => void;
    onDeleteEntity: (rootEntityId: LuisId, entity: SimpleEntityStore) => void;

    /**
     * User role to determine the actions.
     */
    userRole: string;

    /**
     * User role to show in LuisTooltip message.
     */
    displayedUserRole: string;
};

export const MLEntityInfoCell = (props: MLEntityDetailsProps) => {
    const {
        rootEntityId,
        parentEntityId,
        entityStore,
        level,
        isCollapsed,
        kind,
        isSelected,
        onToggleCollapse,
        onToggleEntityCreation,
        onCreateEntity,
        onDeleteEntity,
        onRenameEntity,
        onRenameChildEntity,
        userRole,
        displayedUserRole
    } = props;

    const { localize } = useLocalization();

    const { canWrite, canDeleteData } = mapUserRoleToUserActions(userRole);

    const [isEditingName, setIsEditingName] = React.useState(kind === 'newEntity');
    const [currentName, setCurrentName] = React.useState(entityStore.name);

    const hasChildren = !!entityStore.children.length;

    const { canHaveChildrenBasedOfDepth, canHaveChildrenBasedOfCurrentCount } = getSimpleEntityLimits(entityStore, level);
    const canBeDeleted = level !== 0;

    const handleStartChildCreation = () => {
        if (isCollapsed) {
            onToggleCollapse(entityStore.id as string);
        }

        onToggleEntityCreation(entityStore.id);
    };

    const handleRenameEntity = () => {
        setIsEditingName(false);

        switch (kind) {
            case 'existingEntity':
                if (currentName === '' || currentName === entityStore.name) {
                    setCurrentName(entityStore.name);

                    return;
                }

                if (level === 0) {
                    onRenameEntity(entityStore, currentName);
                } else {
                    onRenameChildEntity(rootEntityId, entityStore.id, currentName);
                }
                break;

            case 'newEntity':
                if (currentName !== '') {
                    onCreateEntity(rootEntityId, parentEntityId, currentName);
                }
                onToggleEntityCreation(null);
                break;

            default:
        }
    };
    const onKeyDown = (event: React.KeyboardEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        if (event.key === LuisKeyCodes.Escape) {
            switch (kind) {
                case 'existingEntity':
                    setIsEditingName(false);
                    setCurrentName(entityStore.name);
                    break;

                case 'newEntity':
                    onToggleEntityCreation(null);
                    break;

                default:
            }
        }
    };

    const renderAddChildButton = () => {
        const addChildButtonBase = (
            <IconButton
                title={localize(messages.addChildTitle)}
                onClick={handleStartChildCreation}
                styles={controlsButtonStyles}
                iconProps={{ iconName: 'Add', styles: addChildEntityButtonIconStyles }}
                disabled={isEditingName || !canHaveChildrenBasedOfCurrentCount || !canWrite}
            />
        );

        if (!canHaveChildrenBasedOfCurrentCount) {
            return <LuisTooltip content={localize(messages.addChildMaxCountTitle)}>{addChildButtonBase}</LuisTooltip>;
        }
        if (!canWrite) {
            return <RBACTooltip displayedUserRole={displayedUserRole} componentSurroundedByToolTip={addChildButtonBase} />;
        }

        return addChildButtonBase;
    };

    return (
        <EntityContainer horizontal grow={1} horizontalAlign="space-between" verticalAlign="center" customProps={{ level }}>
            <NameWrapper horizontal verticalAlign="center">
                <ToggleIcon
                    title={isCollapsed ? localize(messages.expandEntityTitle) : localize(messages.collapseEntityTitle)}
                    iconProps={{ iconName: 'ChevronDown', styles: toggleEntityButtonIconStyles }}
                    customProps={{ isCollapsed }}
                    style={{ visibility: hasChildren ? 'visible' : 'hidden' }}
                    onClick={partial(onToggleCollapse, entityStore.id, !isCollapsed)}
                />
                {isEditingName && canWrite ? (
                    <LuisTextField
                        autoFocus
                        value={currentName}
                        onChange={(_, newValue) => setCurrentName(newValue)}
                        onEnterKeyUp={e => {
                            e.preventDefault();
                            handleRenameEntity();
                        }}
                        onBlur={e => {
                            e.preventDefault();
                            handleRenameEntity();
                        }}
                        onKeyDown={onKeyDown}
                    />
                ) : (
                    <TruncatedText title={entityStore.name} onDoubleClick={() => setIsEditingName(true)}>
                        {entityStore.name}
                    </TruncatedText>
                )}
            </NameWrapper>

            {kind === 'existingEntity' && (
                <ActionsContainer className={isSelected ? null : entityActionsContainerClassName} horizontal horizontalAlign="end">
                    {canHaveChildrenBasedOfDepth && renderAddChildButton()}

                    {canBeDeleted && (
                        <RBACTooltip
                            hidden={canDeleteData}
                            displayedUserRole={displayedUserRole}
                            componentSurroundedByToolTip={
                                <IconButton
                                    title={localize(messages.deleteEntityTitle)}
                                    onClick={() => onDeleteEntity(rootEntityId, entityStore)}
                                    styles={controlsButtonStyles}
                                    iconProps={{ iconName: 'Delete', styles: deleteEntityButtonIconStyles }}
                                    disabled={isEditingName || !canDeleteData}
                                />
                            }
                        />
                    )}
                </ActionsContainer>
            )}
        </EntityContainer>
    );
};
